/**
 * The raw `location.region` values in sites/<id>/data/resorts.json are not clean:
 * many were scraped as sentence fragments such as
 * "Our introduction to Nagano Prefecture" or "Learn more about the Yuzawa & Minamiuonuma region".
 * Anything user-facing that counts or groups regions must normalize first, otherwise
 * "Nagano" and "Our introduction to Nagano Prefecture" count as two different places.
 *
 * Relative imports only: vite.config.ts loads this without the "@" alias.
 */
import type { Language } from '../types/resort';
import { SITE, pick } from '../site';

/** The site's first-level regions (Japan: prefectures), in matching order. */
const PREFECTURES = SITE.regions.map((region) => region.id);

/** Known non-region labels that map onto a region (SiteConfig.regionAliases). */
const REGION_TO_PREFECTURE: Record<string, string> = SITE.regionAliases;

/**
 * Returns the prefecture a raw region string refers to, or null when it carries
 * no usable location (empty, "Coming soon", etc.).
 */
export function normalizeRegion(raw?: string | null): string | null {
  if (!raw) return null;
  const cleaned = raw
    .replace(/^\s*(our introduction to|learn more about( the)?( the)?)\s+/i, '')
    .replace(/\s+(prefecture|region|province)\s*$/i, '')
    .trim();
  if (!cleaned) return null;

  const match = PREFECTURES.find((pref) => new RegExp(`\\b${pref}\\b`, 'i').test(cleaned));
  if (match) return match;

  return REGION_TO_PREFECTURE[cleaned.toLowerCase()] ?? null;
}

/** Distinct, normalized prefectures present in a set of raw region strings. */
export function countRegions(rawRegions: Array<string | null | undefined>): number {
  const set = new Set<string>();
  rawRegions.forEach((raw) => {
    const pref = normalizeRegion(raw);
    if (pref) set.add(pref);
  });
  return set.size;
}

const PREFECTURE_NAMES = Object.fromEntries(SITE.regions.map((region) => [region.id, region.name]));

/** A prefecture key (e.g. "Nagano") in the given language, e.g. 長野県 / 나가노현. */
export function prefectureName(prefecture: string | null, lang: Language): string | null {
  if (!prefecture) return null;
  return pick(PREFECTURE_NAMES[prefecture], lang) || prefecture;
}

/** A raw `location.region` value as a display label in the given language. */
export function regionLabel(raw: string | null | undefined, lang: Language): string {
  return prefectureName(normalizeRegion(raw), lang) ?? (raw ?? '').trim();
}
